import { fetchOfferLocationBreadcrumbs } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getBreadcrumbPropertyFromOffer, isBreadcrumbSourceLocale } from 'lib/offer/locationBreadcrumbs'
import type { BreadcrumbProperty } from 'lib/offer/locationBreadcrumbs'
import { useEffect, useMemo } from 'react'
import { getActiveLocale } from 'selectors/placeSelectors'

export type { BreadcrumbProperty } from 'lib/offer/locationBreadcrumbs'

export function useLocalizedOfferLocation(offer: App.AnyOffer | undefined): string | undefined {
  const locale = useAppSelector(getActiveLocale)
  const properties = useMemo(() => {
    const property = getBreadcrumbPropertyFromOffer(offer)
    return property && !isBreadcrumbSourceLocale(locale) ? [property] : EmptyArray
  }, [offer, locale])

  const locations = useOfferLocationsBreadcrumbs(properties, false)[0]?.locations

  return useMemo(
    () => locations?.map(location => location.name).filter(Boolean).join(', ') || undefined,
    [locations],
  )
}

export function useOfferLocationBreadcrumbs(propertyId: string, type: 'le' | 'bedbank', showAllLocations: boolean) {
  return useOfferLocationsBreadcrumbs([{ propertyId, type }], showAllLocations)[0] ?? []
}

export function useOfferLocationsBreadcrumbs(properties: Array<BreadcrumbProperty>, showAllLocations: boolean): Array<App.LocationBreadcrumbs> {
  const dispatch = useAppDispatch()
  const breadcrumbsDict = useAppSelector(state => state.offer.locationBreadcrumbs)
  const breadcrumbs: Array<App.LocationBreadcrumbs> = useMemo<Array<App.LocationBreadcrumbs>>(() => properties.map(property => breadcrumbsDict[property.propertyId] ?? { locations: [] }), [breadcrumbsDict, properties])
  useEffect(() => {
    for (const property of properties) {
      dispatch(fetchOfferLocationBreadcrumbs(property.propertyId, property.type, showAllLocations))
    }
  }, [properties, showAllLocations, dispatch])
  return breadcrumbs
}
